import type { ConversationStatus, LeadStatus } from '@conversia/shared-types';
import type { CrmConfig } from '@/api/installation.api';

/**
 * Modelo de la ficha del lead — lógica PURA y testeable que la
 * `ContactDetailsPanel` cablea a los endpoints existentes. No hace fetch ni toca
 * el DOM: solo describe opciones, campos editables y las transiciones de estado
 * de las acciones rápidas, para que la UI quede delgada y el comportamiento se
 * pueda verificar con tests de funciones puras (entorno `node`, sin jsdom).
 *
 * Las opciones/labels se derivan de la config de la instalación (`CrmConfig`,
 * vía GET /api/installation-config/crm); las constantes por defecto reproducen
 * las del piloto (OSITO) y son el fallback mientras la config carga.
 */

/** Opciones del dropdown de temperatura comercial (enum `LeadStatus`). */
export const LEAD_STATUS_OPTIONS: { value: LeadStatus; label: string }[] = [
  { value: 'CALIENTE', label: 'Caliente' },
  { value: 'TIBIO', label: 'Tibio' },
  { value: 'FRIO', label: 'Frío' },
  { value: 'CERRADO', label: 'Cerrado' },
  { value: 'NO_CERRADO', label: 'No cerrado' },
];

/**
 * Opciones del dropdown de temperatura con los labels de la instalación. Los
 * valores (enum) son fijos; solo la etiqueta visible es configurable. PURO.
 */
export function leadStatusOptionsFrom(
  crm: CrmConfig | undefined,
): { value: LeadStatus; label: string }[] {
  if (!crm) return LEAD_STATUS_OPTIONS;
  return LEAD_STATUS_OPTIONS.map((o) => ({
    value: o.value,
    label: crm.leadStatusLabels[o.value] ?? o.label,
  }));
}

/**
 * Campos "detectados" que el humano puede corregir/llenar a mano. Las claves
 * coinciden con las que captura el agente en `profileDataJson` (ver
 * notion.service / lead-card). Se persisten con un merge no destructivo vía
 * `PATCH /api/contacts/:id` { profileData }.
 */
export interface EditableLeadField {
  key: string;
  label: string;
  placeholder: string;
}

// Espejo de `defaultCrmConfig()` en el API (config.service.ts). Si cambian los
// campos de la ficha, cambian en los dos lados.
export const EDITABLE_LEAD_FIELDS: EditableLeadField[] = [
  { key: 'producto', label: 'Producto', placeholder: 'Tenis, Crocs, polo…' },
  { key: 'tipo_cliente', label: 'Tipo de cliente', placeholder: 'Detalle o por mayor' },
  { key: 'sucursal', label: 'Sucursal más cercana', placeholder: 'Mar Guayabo, Distrito…' },
  { key: 'modalidad', label: 'Modalidad', placeholder: 'Retiro o delivery' },
  { key: 'zona', label: 'Zona', placeholder: 'Capital o interior, sector exacto' },
  { key: 'forma_pago', label: 'Forma de pago', placeholder: 'Depósito, efectivo…' },
  { key: 'referencia_pago', label: 'Referencia de pago', placeholder: 'Nº de comprobante' },
  { key: 'fecha_comprometida', label: 'Fecha comprometida', placeholder: 'Cuándo lo quiere' },
];

/** Campos editables de la ficha según la config de la instalación. PURO. */
export function editableLeadFieldsFrom(crm: CrmConfig | undefined): EditableLeadField[] {
  if (!crm || crm.editableFields.length === 0) return EDITABLE_LEAD_FIELDS;
  return crm.editableFields;
}

/** Claves que se editan arriba; se excluyen del bloque read-only para no duplicar. */
export function editableLeadKeysFrom(crm: CrmConfig | undefined): ReadonlySet<string> {
  return new Set(editableLeadFieldsFrom(crm).map((f) => f.key));
}

export const EDITABLE_LEAD_KEYS: ReadonlySet<string> = new Set(
  EDITABLE_LEAD_FIELDS.map((f) => f.key),
);

/** Clave donde viven las notas internas (single string) en `profileDataJson`. */
export const INTERNAL_NOTES_KEY = 'notas_internas';

/**
 * Clave reservada de `profileDataJson`: mapa { campo: ISO } con la fecha de la
 * última EDICIÓN MANUAL de cada campo (la sella el server en el PATCH del
 * panel). Sirve para mostrar "editado a mano" y no confundirlo con lo que
 * capturó la IA.
 */
export const MANUAL_EDITS_KEY = '_manual';

/** Mapa { campo: ISO } de ediciones manuales de la ficha, o {} si no hay. PURO. */
export function manualEditsFrom(card: Record<string, unknown> | undefined): Record<string, string> {
  const raw = card?.[MANUAL_EDITS_KEY];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  return Object.fromEntries(
    Object.entries(raw as Record<string, unknown>).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string',
    ),
  );
}

// "Agendar" YA NO es una transición de estado: abre el modal de crear trabajo
// (capa de operaciones, PASO 2) y se maneja aparte en la UI. Aquí quedan solo
// las acciones que mutan estado de la conversación/lead.
export type QuickAction = 'escalate' | 'discard';

/**
 * Transición de una acción rápida, en términos de los enums existentes:
 *  - `escalate` → conversación a WAITING_HUMAN (handoff a un humano), igual que
 *    "Tomar control"; opcionalmente se la asigna al operador actual.
 *  - `discard`  → lead perdido: temperatura NO_CERRADO + conversación CLOSED.
 */
export interface QuickActionTransition {
  action: QuickAction;
  label: string;
  toast: string;
  /** Nuevo `ConversationStatus`, si aplica. */
  status?: ConversationStatus;
  /** Nueva temperatura `LeadStatus`, si aplica. */
  leadStatus?: LeadStatus;
  /** Asignar la conversación al usuario actual (handoff). */
  assignToMe?: boolean;
}

export function quickActionTransition(action: QuickAction): QuickActionTransition {
  switch (action) {
    case 'escalate':
      return {
        action,
        label: 'Escalar',
        toast: 'Conversación escalada a un humano',
        status: 'WAITING_HUMAN',
        assignToMe: true,
      };
    case 'discard':
      return {
        action,
        label: 'Descartar',
        toast: 'Lead descartado',
        status: 'CLOSED',
        leadStatus: 'NO_CERRADO',
      };
  }
}
